import { useCallback, useRef, useState, type ReactNode } from 'react';
import { focusField } from './focusField';

/**
 * The one validation rhythm for every Product 1 form.
 *
 * Pass the current error message for each field (null when valid), computed
 * from the values on every render — typically `t(validationMessageKey(...))`
 * from `@munifocus/utils`. The hook decides *when* each one shows:
 *
 * - not while the person is still typing a field for the first time;
 * - once they leave it (blur);
 * - for every field, once they try to submit — and then focus moves to the
 *   first invalid one, in the order the `errors` object lists them, so list
 *   them in the order they appear on screen.
 *
 * Typing is never blocked: a shown error simply updates as the value changes.
 *
 * `field(key)` gives the props a `TextField` needs: `error` (which MUI turns into
 * `aria-invalid`), `helperText` (wired to `aria-describedby`), `onBlur`, and an
 * `inputRef` for the focus move.
 */
export interface FieldValidation<K extends string> {
  /** The message currently shown for `key`, or undefined. */
  shown(key: K): string | undefined;
  /** Spread onto a `TextField`. `hint` shows when there is no error. */
  field(
    key: K,
    hint?: ReactNode,
  ): {
    error: boolean;
    helperText: ReactNode;
    onBlur: () => void;
    inputRef: (el: HTMLElement | null) => void;
  };
  /** Mark one field as visited (for controls that have no blur of their own). */
  touch(key: K): void;
  /** Register a focus target for a control `field()` is not spread on. */
  ref(key: K): (el: HTMLElement | null) => void;
  /** Call on submit. Shows every error, focuses the first, returns true when there are none. */
  validate(): boolean;
  /** Forget visits and the submit attempt (after a successful send, on reopen). */
  reset(): void;
}

export function useFieldValidation<K extends string>(
  errors: Record<K, string | null | undefined>,
): FieldValidation<K> {
  const [touched, setTouched] = useState<Partial<Record<K, true>>>({});
  const [submitted, setSubmitted] = useState(false);
  const refs = useRef<Partial<Record<K, HTMLElement | null>>>({});
  const latest = useRef(errors);
  latest.current = errors;

  const shown = (key: K): string | undefined =>
    (submitted || touched[key]) && errors[key] ? (errors[key] as string) : undefined;

  const touch = useCallback((key: K) => {
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);

  const ref = useCallback(
    (key: K) => (el: HTMLElement | null) => {
      refs.current[key] = el;
    },
    [],
  );

  const validate = useCallback((): boolean => {
    setSubmitted(true);
    const first = (Object.keys(latest.current) as K[]).find((k) => latest.current[k]);
    if (first === undefined) return true;
    focusField(refs.current[first]);
    return false;
  }, []);

  const reset = useCallback(() => {
    setTouched({});
    setSubmitted(false);
  }, []);

  return {
    shown,
    touch,
    ref,
    validate,
    reset,
    field: (key, hint) => {
      const message = shown(key);
      return {
        error: Boolean(message),
        helperText: message ?? hint,
        onBlur: () => touch(key),
        inputRef: ref(key),
      };
    },
  };
}
